import type { GetServerSidePropsResult, Redirect } from 'next'
import type { RefObject } from 'react'

import Alert from '@rentspree/component-2023.components.organisms.alert'
import { resizeImgProxy } from '@rentspree/helper'
import Head from 'next/head'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { Div } from 'components'
import {
  PropertyDetails,
  ListingGallery,
  ListingOverviewSection,
  ListingLeasingInfoSection,
  ListingFeaturesSection,
  ListingUtilitiesAmenitiesSection,
  ListingMapSection,
  ListingTitleSection,
  ListingVideo,
  ListingApplyPanel,
  HeadWithMetaTags,
  NavbarV2,
  Toast,
  Footer,
} from 'containers'
import { BlockOverlay } from 'containers/public-listing/block-overlay'
import { HeroBanner } from 'containers/public-listing/hero-banner/hero-banner'
import { ScheduleTour } from 'containers/public-listing/listing-schedule-tour/listing-schedule-tour'
import { PendingReviewOverlay } from 'containers/public-listing/pending-review-overlay'
import { Container, Content, Panel, FontWrapper } from 'containers/public-listing/styled'
import { ListingIdProvider } from 'context/listingIdContext'
import { APPROVAL_STATUS } from 'enum/information-type-enum'
import env from 'env'
import { getStoreServerSideProps } from 'store'
import { usePreviewPostMessage } from 'store/@hooks/use-preview-post-message'
import { useTrackEnterPage } from 'store/@hooks/use-tracker'
import { actions as agentProfileActions } from 'store/agent-profile'
import { actions, selectSharingMetaProperties, selectPublicListing } from 'store/public-listing'
import { events } from 'tracker'
import { getStatsigInitializeValues } from 'utils/feature-flag/instance'
import { SlugValidator } from 'utils/slug-validator'

interface IPublicPage {
  isPreview?: boolean
  listingId?: string
  isBlocked?: boolean
  isPendingReview?: boolean
  slug?: string
  isReviewMode?: boolean
}
export const EVENT_NAME = {
  USE_EFFECT: 'USE_EFFECT',
  ON_READY: 'ON_READY',
  ON_TOGGLE: 'ON_TOGGLE',
  ON_MESSAGE: 'ON_MESSAGE',
  CALCULATE_HEIGHT: 'CALCULATE_HEIGHT',
}

const PublicListingPage: React.FC<IPublicPage> = ({
  isPreview,
  listingId,
  isBlocked,
  isPendingReview,
  isReviewMode,
  ...props
}) => {
  const dispatch = useDispatch()
  const listing = useSelector(selectPublicListing)
  const imageProxyHost: string = env.IMAGE_PROXY_HOST || 'https://image-proxy.dev.rentspree.dev'
  const imageProxyUrl: string = env.IMAGE_PROXY_URL || 'https://storage.googleapis.com'
  const protocol = env.GCS_PROTOCOL || 'gs://'
  const imageWidth = Number(env.IMAGE_PROXY_RESIZE_WIDTH) || 1200
  const imageHeight = Number(env.IMAGE_PROXY_RESIZE_HEIGHT) || 800
  const sectionRef = useRef<null | HTMLDivElement>(null)
  const { LISTING_PUBLIC_PAGE } = events

  useTrackEnterPage(LISTING_PUBLIC_PAGE.VIEW_LISTING, {
    listing_id: listingId,
  })

  const calculateHeight = (currentRef: RefObject<HTMLDivElement>): number => {
    if (currentRef.current) {
      return currentRef.current.offsetTop + currentRef.current.clientHeight
    }
    return 0
  }

  usePreviewPostMessage(sectionRef, calculateHeight)

  const metaData = useSelector(selectSharingMetaProperties)

  const renderOverlay = () => {
    if (isPreview && !listing.slug && props.slug) {
      dispatch(
        actions.getDraftedListing({
          slug: props.slug,
        }),
      )
      return null
    }
    if (isReviewMode && !listing.slug && props.slug) {
      dispatch(
        actions.getReviewListing({
          slug: props.slug,
        }),
      )
      return null
    }
    if (isBlocked) {
      return <BlockOverlay />
    }
    if (isPendingReview && !isReviewMode) {
      return <PendingReviewOverlay />
    }

    const isPreviewOrReviewMode = isPreview || isReviewMode

    return (
      <>
        <Div
          ref={sectionRef}
          className="w-100 d-flex flex-column align-items-center"
          $mt={isReviewMode ? '20' : '40'}
          $mb={isReviewMode ? '0' : '90'}
        >
          <Container>
            {isReviewMode && (
              <Div $mb="20" className="w-100">
                <Alert severity="warning" title="Review Mode" />
              </Div>
            )}
            <Content id="listing-info">
              <Panel width="604px">
                <ListingGallery canFullScreen={!isPreviewOrReviewMode} />
                <HeroBanner $margin="20px 0 0 0">
                  <ListingVideo isPreview={isPreviewOrReviewMode} />
                  <ScheduleTour isPreview={isPreviewOrReviewMode} />
                </HeroBanner>
                <ListingTitleSection />
                <PropertyDetails />
                <ListingApplyPanel mobileRender isPreview={isPreviewOrReviewMode} />
                <ListingOverviewSection />
                <ListingLeasingInfoSection />
                <ListingFeaturesSection />
                <ListingUtilitiesAmenitiesSection />
              </Panel>
              <ListingApplyPanel isPreview={isPreviewOrReviewMode} />
            </Content>
            <Content>
              <ListingMapSection isPreview={isPreview} />
            </Content>
          </Container>
        </Div>
        {!isPreviewOrReviewMode && <Footer />}
      </>
    )
  }

  metaData.image = resizeImgProxy(
    metaData?.image ? metaData.image : '',
    imageWidth,
    imageHeight,
    imageProxyHost,
    imageProxyUrl,
    protocol,
  )

  return (
    <ListingIdProvider listingId={listingId ?? '-'}>
      <>
        <HeadWithMetaTags metaContents={metaData} title={metaData.title} />
        <Head>
          <link
            href="https://fonts.googleapis.com/css2?family=Poppins:wght@100;200;300;400;500;600;700;800&display=swap"
            rel="stylesheet"
          />
        </Head>
        {!isPreview && <NavbarV2 />}
        <Toast />
        <FontWrapper>{renderOverlay()}</FontWrapper>
      </>
    </ListingIdProvider>
  )
}

export const getServerSideProps = getStoreServerSideProps<Record<string, unknown>>((store) => {
  const PREVIEW_QUERY_PARAM = 'preview'
  const REVIEW_MODE_QUERY_PARAM = 'review-mode'
  const redirectToHome: GetServerSidePropsResult<Redirect> = {
    redirect: { destination: env.BASE_URL, permanent: false },
  }

  const createListingPageProps = (
    isPreview: boolean,
    listing: PublicListing,
    isReviewMode: boolean,
  ) => {
    const isPendingReview = listing?.approvalStatus === APPROVAL_STATUS.PENDING
    const props = {
      isPreview,
      listingId: listing?.id,
      isBlocked: listing?.approvalStatus === APPROVAL_STATUS.BLOCKED,
      isPendingReview,
      isReviewMode: isPendingReview && isReviewMode,
    }
    return props
  }

  const getPublicListing = async (slug: string): Promise<PublicListing> => {
    const publicListing = await store
      .dispatch(
        actions.getPublicListing({
          slug,
        }),
      )
      .unwrap()
    return publicListing
  }

  const setAgentProfile = (listing: PublicListing): void => {
    store.dispatch(
      agentProfileActions.setAgentProfile({
        email: listing.agentProfile?.email,
        userId: listing.agentProfile?.userId,
      }),
    )
  }

  const validSlug = (slug: string): boolean => {
    const slugValidator = new SlugValidator()
    return slugValidator.isSlugValid(slug)
  }

  const isPublicListing = (listing: PublicListing) => listing.id && listing.slug

  return async (context) => {
    const { params: { slug } = {}, query: { view } = {} } = context
    const parsedSlug: string = (slug || '') as string

    if (!validSlug(parsedSlug)) {
      return redirectToHome
    }

    const initializeValues = await getStatsigInitializeValues()

    const isPreview = view === PREVIEW_QUERY_PARAM
    const isReviewMode = view === REVIEW_MODE_QUERY_PARAM
    if (isPreview || isReviewMode) {
      const splittedSlug = parsedSlug.split('-')
      return {
        props: {
          isPreview,
          listingId: splittedSlug[splittedSlug.length - 1],
          slug: parsedSlug,
          initializeValues,
          isReviewMode,
        },
      }
    }

    try {
      const publicListing = await getPublicListing(parsedSlug)

      if (!isPublicListing(publicListing)) {
        return redirectToHome
      }

      setAgentProfile(publicListing)

      return {
        props: {
          ...createListingPageProps(isPreview, publicListing, isReviewMode),
          initializeValues,
        },
      }
    } catch (e) {
      return { notFound: true }
    }
  }
})

PublicListingPage.propTypes = {
  isPreview: PropTypes.bool,
  listingId: PropTypes.string,
  isBlocked: PropTypes.bool,
  isPendingReview: PropTypes.bool,
  isReviewMode: PropTypes.bool,
}

PublicListingPage.defaultProps = {
  isPreview: false,
  listingId: '-',
  isBlocked: false,
  isPendingReview: false,
  isReviewMode: false,
}

export default PublicListingPage
